/* Patch 15.1 — Reconciliation Investigation Drawer
   ====================================================
   Forensic drill-down panel: shows root cause, affected documents,
   variance amount, and suggested actions for a single reconciliation
   check. Slides in from the right when user clicks "🔍 Investigate"
   on a FAIL/WARNING row.

   IMPORTANT (per CFO mandate): suggested actions are ADVISORY ONLY —
   they describe what a human should do, never auto-execute anything. */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  X, Loader2, ExternalLink, AlertCircle, Lightbulb, FileText, Users, Play, CheckCircle2,
} from 'lucide-react';

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

// Map a Reconciliation check name (as returned by the engine) to the
// short investigator key the backend expects.
export const CHECK_NAME_TO_KEY = {
  'Revenue ↔ Invoices':              'revenue',
  'AR ↔ Customer Balances':          'ar',
  'Cash ↔ Payments':                 'cash',
  'VAT Ledger ↔ VAT Returns':        'vat',
  'Project Profit ↔ General Ledger': 'projects',
  'Overhead Allocation ↔ 53xxx':     'overhead',
};

export default function InvestigationDrawer({ open, onClose, period, checkName }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningAction, setRunningAction] = useState(null);
  const key = CHECK_NAME_TO_KEY[checkName];

  // Map a forensic action_id → an executable backend call. Only actions
  // listed here render an "Execute" button; everything else stays
  // advisory-only (per CFO mandate). Each entry returns a string toast
  // message on success and is wrapped in a confirm() prompt.
  //
  // NOTE: /accounting/backfill is a single endpoint that posts journals
  // for EVERY approved doc category (invoices, supplier_bills, payments,
  // expenses, payroll). Both AR and Cash variances therefore share the
  // same runner — one click fixes the whole batch.
  const _runBackfill = async (label) => {
    const r = await axiosInstance.post('/accounting/backfill');
    const d = r.data || {};
    return `✅ ${label} — فواتير: ${d.invoices || 0} · مدفوعات: ${d.payments || 0} · فواتير مورّد: ${d.supplier_bills || 0} · مصروفات: ${d.expenses || 0} · رواتب: ${d.payroll || 0}${(d.errors || []).length ? ` · أخطاء: ${d.errors.length}` : ''}`;
  };
  const ACTION_RUNNERS = {
    BACKFILL_PAYMENT_JOURNALS: {
      confirm: 'سيتم تشغيل engine.post_payment على كل دفعة معتمدة بدون قيد. آمن — لن يُحذف قيد موجود ولا يُلمَس قيد يدوي. متابعة؟',
      run: () => _runBackfill('تم إنشاء قيود المدفوعات'),
    },
    BACKFILL_INVOICE_JOURNALS: {
      confirm: 'سيتم تشغيل engine.post_customer_invoice على كل فاتورة معتمدة بدون قيد. آمن — لن يُحذف قيد موجود ولا يُلمَس قيد يدوي. متابعة؟',
      run: () => _runBackfill('تم إنشاء قيود الفواتير'),
    },
    // (STA-5) VAT Desync backfill — appends missing VAT_INPUT lines
    // to bill journals so GL matches the filed return.
    BACKFILL_VAT_DESYNC: {
      confirm: 'سيتم إضافة خطوط VAT_INPUT ناقصة إلى قيود الفواتير في هذا الربع. لن تتغير قيمة الإقرار المُقدَّم (مجمّد). متابعة؟',
      run: async () => {
        if (!data?.desync?.return_id) throw new Error('No return_id in context');
        const r = await axiosInstance.post(
          `/vat/returns/${data.desync.return_id}/desync/backfill`);
        return `✅ تم إصلاح ${r.data.repaired.length} قيد · أُضيف ${fmt(r.data.total_added)} SAR إلى GL`;
      },
    },
    // (Feb 2026) Widen the desync scan window by ±90 days. Read-only —
    // never mutates GL. Reveals late-posted or back-dated journals that
    // still belong to this return but landed outside its window.
    WIDEN_DRILL_WINDOW: {
      confirm: 'سيتم إعادة الفحص التشخيصي مع توسيع نافذة التاريخ إلى ±90 يوماً — قراءة فقط. متابعة؟',
      run: async () => {
        const returnId = data?.desync?.return_id
          || data?.affected_documents?.[0]?.return_id;
        if (!returnId) {
          throw new Error(
            'No filed VAT return in context — Widen Drill Window is only '
            + 'available when the investigator has resolved a return_id.'
          );
        }
        const r = await axiosInstance.get(
          `/vat/returns/${returnId}/desync/diagnose?widen_days=90`);
        const extra = (r.data?.journals_outside_window || []).length;
        const sum = (r.data?.journals_outside_window || [])
          .reduce((s, j) => s + Number(j?.vat_sum || 0), 0);
        return `🕰️ نافذة موسَّعة إلى ±90 يوم · ${extra} قيد خارج النافذة · Σ VAT: ${fmt(sum)} SAR`;
      },
    },
    // (Feb 2026) Read-only "what-if" preview: what would the return
    // look like if bucketed by GL journal_date instead of bill_date?
    // Never mutates the return.
    RE_FILE_ON_POSTING_DATE: {
      confirm: 'معاينة للقراءة فقط: كيف سيبدو الإقرار لو أُعيد ترتيبه على تاريخ القيد بدل تاريخ الفاتورة؟ لن يتم تعديل الإقرار المُقدَّم.',
      run: async () => {
        const returnId = data?.desync?.return_id
          || data?.affected_documents?.[0]?.return_id;
        if (!returnId) {
          throw new Error(
            'No filed VAT return in context — Re-file preview requires '
            + 'a resolved return_id.'
          );
        }
        const r = await axiosInstance.get(
          `/vat/returns/${returnId}/preview-by-posting-date`);
        const dNet = r.data?.delta?.net;
        return `📅 معاينة posting-date · Δ صافي: ${fmt(dNet)} SAR (input Δ: ${fmt(r.data?.delta?.input_vat)}, output Δ: ${fmt(r.data?.delta?.output_vat)})`;
      },
    },
  };

  const runAction = async (action_id) => {
    const runner = ACTION_RUNNERS[action_id];
    if (!runner) return;
    if (!window.confirm(runner.confirm)) return;
    setRunningAction(action_id);
    try {
      const msg = await runner.run();
      toast.success(msg);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || e?.message || 'فشل التنفيذ'));
    } finally { setRunningAction(null); }
  };

  useEffect(() => {
    if (!open || !key) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setData(null);
      try {
        const r = await axiosInstance.get(
          `/reconciliation/investigate?period=${period}&check=${key}`
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(`Investigation failed: ${e.response?.data?.detail || e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, period, key]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="investigation-drawer">
      <button
        className="flex-1 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close drawer"
      />
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-xs uppercase text-slate-500 tracking-wider">
              Forensic Investigation · {period}
            </div>
            <h2 className="text-lg font-bold text-slate-900 truncate">
              {checkName}
            </h2>
          </div>
          <button
            data-testid="close-investigation-drawer"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Investigating…
            </div>
          )}

          {!loading && data && (
            <>
              {data.clean ? (
                <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="investigation-clean">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs uppercase font-bold text-emerald-700 tracking-wider mb-1">
                        No findings
                      </div>
                      <p className="text-sm text-emerald-900 leading-relaxed">
                        {data.note || 'This ledger reconciles fully — nothing to investigate for this period.'}
                      </p>
                    </div>
                  </div>
                </section>
              ) : data.root_cause ? (
                <section className="bg-rose-50 border border-rose-200 rounded-lg p-4" data-testid="investigation-root-cause">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-rose-700 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs uppercase font-bold text-rose-700 tracking-wider mb-1">
                        Root Cause
                      </div>
                      <p className="text-sm text-rose-900 leading-relaxed">{data.root_cause}</p>
                    </div>
                  </div>
                </section>
              ) : null}

              <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="investigation-variance">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase text-slate-500 tracking-wider">Variance Amount</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-1">
                      {fmt(data.variance_amount)} <span className="text-sm font-normal text-slate-500">SAR</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase text-slate-500 tracking-wider">Affected Docs</div>
                    <div className="text-2xl font-bold text-slate-900 mt-1">
                      {data.affected_documents?.length || 0}
                      {data.documents_over_cap > 0 && (
                        <span className="text-xs text-slate-500 ml-1">
                          + {data.documents_over_cap} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {data.affected_documents?.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-lg">
                  <h3 className="text-sm font-bold text-slate-900 px-4 pt-4 pb-2 flex items-center gap-2">
                    <FileText size={14} /> Affected Documents
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="affected-docs-table">
                      <thead>
                        <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                          <th className="px-3 py-2">Document</th>
                          <th className="px-3 py-2">Party</th>
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.affected_documents.map((d, i) => (
                          <tr key={d.id || i} className="border-t border-slate-100" data-testid={`affected-doc-${i}`}>
                            <td className="px-3 py-2 font-mono font-medium">{d.label}</td>
                            <td className="px-3 py-2 truncate max-w-[180px]">{d.party || '—'}</td>
                            <td className="px-3 py-2 text-slate-500">{d.date || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmt(d.amount)}</td>
                            <td className="px-3 py-2 text-center">
                              {d.link && (
                                <a
                                  href={d.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sky-600 hover:text-sky-800 inline-flex"
                                  title="Open document"
                                >
                                  <ExternalLink size={12} />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {data.drift_by_customer?.length > 0 && (
                <section className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Users size={14} /> Drift by Customer (Top 10)
                  </h3>
                  <table className="w-full text-xs font-mono" data-testid="drift-by-customer-table">
                    <thead>
                      <tr className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                        <th className="px-2 py-1.5">Customer</th>
                        <th className="px-2 py-1.5 text-right">Invoices</th>
                        <th className="px-2 py-1.5 text-right">Amount (SAR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.drift_by_customer.map((c, i) => (
                        <tr key={c.customer_id || i} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-sans">{c.customer_name}</td>
                          <td className="px-2 py-1.5 text-right">{c.invoice_count}</td>
                          <td className="px-2 py-1.5 text-right font-bold">{fmt(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {data.suggested_actions?.length > 0 && (
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="suggested-actions">
                  <h3 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <Lightbulb size={14} /> Suggested Actions
                    <span className="text-[10px] font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      Advisory — requires human approval
                    </span>
                  </h3>
                  <ul className="space-y-2">
                    {data.suggested_actions.map((a, i) => (
                      <li
                        key={i}
                        className="bg-white/70 border border-amber-200 rounded p-2.5"
                        data-testid={`suggested-action-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-sm font-bold text-amber-900">{a.label}</div>
                            {a.why && (
                              <div className="text-xs text-amber-800 mt-0.5">{a.why}</div>
                            )}
                            <div className="text-[10px] text-amber-600 font-mono mt-1">
                              {a.action_id}
                            </div>
                          </div>
                          {ACTION_RUNNERS[a.action_id] && (
                            <button
                              data-testid={`run-action-${a.action_id}`}
                              onClick={() => runAction(a.action_id)}
                              disabled={runningAction === a.action_id}
                              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                              title="Execute this remediation (admin)"
                            >
                              {runningAction === a.action_id
                                ? <Loader2 size={12} className="animate-spin" />
                                : <Play size={12} />}
                              تنفيذ
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {data.note && (
                <p className="text-xs text-slate-500 italic px-1">{data.note}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
